import _ from 'lodash';
import { MarAsset } from 'types/rest/marFilesTypes';
import { OperatorType } from 'store/marFiles/marFilesTypes';
import { SUPPORTED_KEYSTORES } from '../constants/bzm-constants';

export const isDirty = (asset: MarAsset): boolean => {
  if (isFieldDirty(asset.description, asset.original.description)) return true;
  if (isFieldDirty(asset.serviceId, asset.original.serviceId)) return true;
  return areTagsDirty(asset.tags, asset.original.tags);
};

export const buildPatchBody = (asset: MarAsset): any => {
  const patchBody: any = {};

  if (isFieldDirty(asset.description, asset.original.description)) patchBody['description'] = asset.description;
  if (isFieldDirty(asset.serviceId, asset.original.serviceId)) patchBody['serviceId'] = asset.serviceId;
  if (areTagsDirty(asset.tags, asset.original.tags)) patchBody['tags'] = asset.tags;

  return patchBody;
};

const isFieldDirty = (field: any, originalField: any): boolean => {
  return (field && !originalField) || (!field && originalField) || (field && originalField && field !== originalField);
};

const areTagsDirty = (tags: string[], originalTags: string[]): boolean => {
  let foundDifference = false;
  _.each(tags, tag => {
    //Roll through the new tags to detect any new tags
    const foundTag = _.find(originalTags, origTag => origTag === tag);
    if (!foundTag) {
      foundDifference = true;
    }
  });

  if (!foundDifference) {
    _.each(originalTags, tag => {
      //Roll through the original tasgs to detect any removed tags
      const foundTag = _.find(tags, origTag => origTag === tag);
      if (!foundTag) {
        foundDifference = true;
      }
    });
  }

  return foundDifference;
};

/**
 * Parse argument type string, which comes from devtest to user-friendly representation.
 * @param argumentMatcher string in the following form: 'arg:ANY;false;;;CASE_INSENSITIVE';
 */
export const parseArgumentOperator = (argumentMatcher: string | null) => {
  const noMatchFoundChar = '-';

  if (argumentMatcher == null) {
    return noMatchFoundChar;
  }

  try {
    const tokens: string[] = argumentMatcher.split(';', 1);
    const matcherName: string = tokens[0].replace('arg:', '');

    //Cast string to operator enumeration
    const operatorEnum: OperatorType = (OperatorType as any)[matcherName.toUpperCase()];
    return operatorEnum ? operatorEnum : noMatchFoundChar;
  } catch (error) {
    return noMatchFoundChar;
  }
};

/**
 * Trims mar extension from filename.
 * @param marFilename
 */
export const trimMarExtensionFromFilename = (marFilename: string): string => {
  if (marFilename.toLowerCase().endsWith('.mar')) {
    marFilename = marFilename.slice(0, -4);
  }
  return marFilename;
};

/**
 * Checks if the file extension matches a keystore file.
 * @param fileName
 */
export const isKeystoreFile = (fileName: string) => {
  if (_.isNil(fileName)) {
    return false;
  }

  const ext = fileName
    .toLowerCase()
    .split('.')
    .pop();
  const foundIndex = _.findIndex(
    SUPPORTED_KEYSTORES,
    supportedKeystoreExt => supportedKeystoreExt.toLowerCase() === '.' + ext,
  );
  return foundIndex !== -1;
};

export const isAssetKeystorePasswordError = (error: string) => {
  return error === 'Unable to read keystore, bad keystore password.' || error === 'Asset keystore file is invalid.';
};

export const isAssetKeystoreAliasError = (error: string) => {
  return _.startsWith(error, 'Unable to read private key alias');
};
